import { CardType } from 'types/deck'

import ManaSymbol from 'components/misc/customIcons/ManaSymbol'

import { calculateManaCurve } from 'utils/manaCurve'

import { RARITY_WEIGHTS, RarityOptionType } from 'types/card'
import { REVERSE_COLOR_MAP } from './charts.const'

import styles from './selectCardsByChartType.module.scss'
import { Color } from 'services/apiTypes/card.types'
import ManaFrontIcon from 'components/misc/customIcons/ManaFont'

export type SliceType = 'color' | 'types' | 'subtypes' | 'categories' | 'rarity' | 'power' | 'toughness' | 'loyalty'
export type GroupedData = {
  cards: CardType[]
  key: string
  label: React.ReactNode
  className?: string
}

export type SelectCardsByChartType = {
  groupedData: GroupedData[]
  relativeMax: number
}

export const selectCardsByChartType = (cards: CardType[], sliceType: SliceType): SelectCardsByChartType => {
  let groupedData: GroupedData[] = []

  if (sliceType === 'color') groupedData = selectDataByColor(cards)
  if (sliceType === 'types') groupedData = selectDataByCardMap(cards, 'types', sliceType)
  if (sliceType === 'subtypes') groupedData = selectDataByCardMap(cards, 'subTypes', sliceType)
  if (sliceType === 'categories') groupedData = selectDataByCardMap(cards, 'categories', sliceType)
  if (sliceType === 'rarity') groupedData = selectDataByCardMap(cards, 'rarity', sliceType)
  if (sliceType === 'power') groupedData = selectDataByCardMap(cards, 'power', sliceType)
  if (sliceType === 'toughness') groupedData = selectDataByCardMap(cards, 'toughness', sliceType)
  if (sliceType === 'loyalty') groupedData = selectDataByCardMap(cards, 'loyalty', sliceType)

  let relativeMax = 0

  for (const group of groupedData) {
    const { data: manaChartData } = calculateManaCurve(group.cards)
    const tallestBar = manaChartData.reduce((largest, { count }) => (count > largest ? count : largest), 0)

    if (tallestBar > relativeMax) relativeMax = tallestBar
  }

  return { groupedData, relativeMax }
}

export const ColorChartLabels: Record<Color | 'Multicolor', React.ReactNode> = {
  White: (
    <label className={styles.chartLabel}>
      <ManaSymbol symbol="W" /> White Spells
    </label>
  ),
  Blue: (
    <label className={styles.chartLabel}>
      <ManaSymbol symbol="U" /> Blue Spells
    </label>
  ),
  Black: (
    <label className={styles.chartLabel}>
      <ManaSymbol symbol="B" /> Black Spells
    </label>
  ),
  Red: (
    <label className={styles.chartLabel}>
      <ManaSymbol symbol="R" /> Red Spells
    </label>
  ),
  Green: (
    <label className={styles.chartLabel}>
      <ManaSymbol symbol="G" /> Green Spells
    </label>
  ),
  Colorless: (
    <label className={styles.chartLabel}>
      <ManaSymbol symbol="C" /> Colorless Spells
    </label>
  ),
  Multicolor: (
    <label className={styles.chartLabel}>
      <ManaFrontIcon icon="multicolor" /> Multicolor Spells
    </label>
  ),
}

// This one is custom due to the custom labels & groupings
const selectDataByColor = (cards: CardType[]): GroupedData[] => {
  return [
    {
      key: REVERSE_COLOR_MAP.White,
      cards: cards.filter(c => c.colors.includes('White')),
      className: styles.white,
      label: ColorChartLabels.White,
    },
    {
      key: REVERSE_COLOR_MAP.Blue,
      cards: cards.filter(c => c.colors.includes('Blue')),
      className: styles.blue,
      label: ColorChartLabels.Blue,
    },
    {
      key: REVERSE_COLOR_MAP.Black,
      cards: cards.filter(c => c.colors.includes('Black')),
      className: styles.black,
      label: ColorChartLabels.Black,
    },
    {
      key: REVERSE_COLOR_MAP.Red,
      cards: cards.filter(c => c.colors.includes('Red')),
      className: styles.red,
      label: ColorChartLabels.Red,
    },
    {
      key: REVERSE_COLOR_MAP.Green,
      cards: cards.filter(c => c.colors.includes('Green')),
      className: styles.green,
      label: ColorChartLabels.Green,
    },
    {
      key: 'C',
      cards: cards.filter(c => !c.colors.length),
      className: styles.colorless,
      label: ColorChartLabels.Colorless,
    },
  ]
}

const selectDataByCardMap = (cards: CardType[], key: keyof CardType, sliceType: SliceType): GroupedData[] => {
  const groups: Record<string, CardType[]> = {}

  for (const card of cards) {
    let data: any = card[key]

    if (!Array.isArray(data)) data = [data]

    for (const item of data) {
      if (item === undefined || item === null || item === '') continue

      const dataAsKey = `${item}`

      if (!groups[dataAsKey]) groups[dataAsKey] = []

      groups[dataAsKey].push(card)
    }
  }

  return Object.entries(groups)
    .map(([key, cards]) => ({
      key,
      cards,
      label: <label className={styles.chartLabel}>{serializeKey(key, sliceType)}</label>,
    }))
    .sort((a, b) => {
      // prettier-ignore
      if (sliceType === 'rarity') return RARITY_WEIGHTS[capitalize(a.key) as RarityOptionType] - RARITY_WEIGHTS[capitalize(b.key) as RarityOptionType]

      return a.key.localeCompare(b.key)
    })
}

function capitalize(s: string) {
  return s && s[0].toUpperCase() + s.slice(1)
}

function serializeKey(key: string, sliceType: SliceType): string {
  if (sliceType === 'rarity') return capitalize(key)
  if (sliceType === 'power') return `Power: ${key}`
  if (sliceType === 'toughness') return `Toughness: ${key}`
  if (sliceType === 'loyalty') return `Loyalty: ${key}`

  return key
}
